import {
  CardMedia,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@material-ui/core";
import { ReactComponent as RedTrashIcon } from "assets/icon/red_trash.svg";
import { Box, BrowseButton, IconButton, Link, Typography } from "components";
import {
  compose,
  defaultProps,
  withHooks,
  withStores,
  withTranslation,
} from "enhancers";
import env from "env";
import { TFunction } from "i18next";
import { AppColor } from "theme/app-color";
import { convertFileToUrl } from "utils/helper";

interface SingleFileUploadProps {
  t: TFunction;
  label: string;
  addButtonLabel: string;
  download: () => void;
  addDocument: any;
  removeDocument: () => void;
  hasFile: boolean;
  accept: string | string[];
  fileName: string;
  openPreview: boolean;
  handleOpenPreview: () => void;
  handleClosePreview: () => void;
  url?: string;
  canPreview: boolean;
  startIcon?: string;
  color?: string;
}

const SingleFileUpload = ({
  label,
  addButtonLabel,
  addDocument,
  removeDocument,
  hasFile,
  accept,
  fileName,
  openPreview,
  handleOpenPreview,
  handleClosePreview,
  url,
  canPreview,
  startIcon,
  color,
}: SingleFileUploadProps) => (
  <Box display="flex" flexDirection="column" alignItems="flex-start">
    <Box display="flex" alignItems="center" mt="4px" style={{ gap: "16px" }}>
      {label && !hasFile && (
        <Typography variant="caption" color={AppColor["Text/Line"]}>
          {label}
        </Typography>
      )}
      {!hasFile && (
        <BrowseButton
          color={color}
          startIcon={startIcon}
          onBrowse={addDocument}
          variant="outlined"
          accept={accept}
          multiple={false}
        >
          {addButtonLabel}
        </BrowseButton>
      )}
      {hasFile && (
        <>
          {canPreview ? (
            <Link type="button" component="button" onClick={handleOpenPreview}>
              <Typography
                color={AppColor["Other/Info"]}
                variant="caption"
                style={{ textDecoration: "underline" }}
                noWrap
              >
                {fileName}
              </Typography>
            </Link>
          ) : (
            <Typography variant="caption" noWrap>
              {fileName}
            </Typography>
          )}

          <IconButton
            size="small"
            onClick={() => {
              removeDocument();
            }}
          >
            <RedTrashIcon width={18} height={18} />
          </IconButton>
        </>
      )}
      <Dialog open={openPreview} onClose={handleClosePreview}>
        <DialogTitle />
        <DialogContent>
          <CardMedia
            component="img"
            image={url}
            style={{
              width: 200,
              height: 200,
              borderRadius: 8,
              backgroundColor: AppColor["Background/Background"],
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  </Box>
);

const enhancer = compose(
  defaultProps({
    alt: "image",
  }),
  withTranslation({ prefix: "components.advance.SingleFileUpload" }),
  withHooks((props: any, hooks: any) => {
    const {
      t,
      accept,
      addButtonLabel,
      label,
      form,
      field,
      setInitialValues,
      canPreview = false,
      startIcon = undefined,
      color = undefined,
    } = props;

    const { setFieldValue, values, resetForm, initialValues } = form;
    const { name: fieldName, value: fieldValue } = field;

    const { useCallback, useState, useEffect } = hooks;

    const [fileName, setFileName] = useState("");
    const [isFileExist, setFileExist] = useState(false);
    const [openPreview, setOpenPreview] = useState(false);
    const [fileUrl, setFileUrl] = useState("");

    const handleOpenPreview = useCallback(() => {
      setOpenPreview(true);
    }, []);

    const handleClosePreview = useCallback(() => {
      setOpenPreview(false);
    }, []);

    const addDocument = useCallback(
      (file: any) => {
        // handleChange(path, file);
        setFileName(file.name);

        setFileExist(true);
        if (fieldName) {
          setFieldValue(fieldName, file);
          setFileUrl(convertFileToUrl(file));
        }
      },
      [fieldName, setFieldValue]
    );

    const removeDocument = useCallback(
      (index: number) => {
        // handleChange(path, null);
        setFileName("");
        setFileExist(false);
        setFieldValue(fieldName, null);
        setFileUrl("");
      },
      [fieldName, setFieldValue]
    );

    useEffect(() => {
      if (fieldValue?.fileName && fieldValue?.url) {
        const loadFile = async () => {
          const newUrl = `${fieldValue?.url}`;
          const blob = await fetch(newUrl).then((r) => r.blob());
          const file = new File([blob], fieldValue?.fileName, {
            type: blob.type,
          });

          setFileExist(true);
          setFileName(fieldValue?.fileName);
          resetForm({
            values: {
              ...initialValues,
              [fieldName]: file,
            },
          });
          setFileUrl(convertFileToUrl(file));
        };
        loadFile();
      }
    }, [fieldName, fieldValue, initialValues, resetForm]);

    return {
      canPreview,

      label,
      hasFile: isFileExist,
      addDocument,
      removeDocument,
      addButtonLabel: addButtonLabel || t(".uploadFile"),
      accept: accept || "image/*",
      fileName,

      openPreview,
      handleOpenPreview,
      handleClosePreview,
      url: fileUrl,

      startIcon,
      color,
    };
  })
);

export default enhancer(SingleFileUpload);
